import { Tab } from "@mui/material"
import { TabPanel } from "@mui/lab"
import { registerTab } from "lib/@components/tabs"
import { registerFeatureWithDescription, registerFeature } from "lib/features"
import { hasDemand } from "lib/authorization/has-demand"
import { isEnabled } from "lib/@components/feature"
import { ClientTabs, ClientPanels } from "slot-definitions"
import { IdpPanel } from "routes/settings/plugins/sso/components/idp-panel"

export const FEATURE_FLAG_SSO = registerFeatureWithDescription({
    numericalId: 300,
    // `-all-users` so the platform propagates it to every session (SFG20 staff included),
    // not just users whose client carries an SSO auth bundle. This is a platform-wide switch;
    // per-client SSO entitlement is the tier-3 account (feature-identity-provider).
    id: "sso-all-users",
    name: "Identity Provider (SSO + SCIM)",
    readyForBeta: false,
    readyForProduction: true,
    descriptionLines: [
        "Enable SAML 2.0 Single Sign-On so users can log in with their corporate IdP (Entra ID, Okta, etc.).",
        "Enable SCIM 2.0 user provisioning so the same IdP can push users, groups, and deprovisions automatically.",
        "Group → license/role mappings let admins control how SCIM group membership drives FIQ access.",
    ],
})

export const FEATURE_FLAG_SSO_CLIENT_CONFIG = registerFeature({
    numericalId: 301,
    name: "SSO Client Self-Service Configuration",
    id: "sso-client-config",
    description: "Allow clients to configure their own SSO settings",
    readyForBeta: false,
    readyForProduction: true,
})

export const FEATURE_FLAG_IDENTITY_PROVIDER_ENTITLEMENT = registerFeatureWithDescription({
    numericalId: 302,
    id: "identity-provider",
    name: "Identity Provider Product Entitlement",
    readyForProduction: true,
    hideInList: true,
    descriptionLines: ["Client-level entitlement for Identity Provider SSO and SCIM configuration."],
})

// Client's own admin settings (/app/admin): a clientAdmin can self-configure SSO only when
// the self-service flag is on for them. With it off, the whole Identity Provider tab is
// hidden (not just paywalled) so client admins cannot reach the config surface.
// hasDemand("feature-identity-provider") cannot be used here — that demand lives on the
// client RECORD (checked server-side by identityProviderAvailable), not in the user's
// session demands. IdpPanel handles the paywall internally when the client lacks the tier.
registerTab({
    tab: "clientAdmin",
    id: "idp",
    title: "Identity Provider",
    content: <IdpPanel $lazy priority={200} />,
    predicate: () => hasDemand("clientAdmin") && isEnabled(FEATURE_FLAG_SSO_CLIENT_CONFIG),
})

// SFG20-admin company-detail view (/app/sfg20-admin/clients/:id): any SFG20 ops user who can
// manage companies can configure SSO for a client while the SSO feature is globally enabled.
// FEATURE_FLAG_SSO is the `sso-all-users` flag, so it reaches every session (SFG20 staff have
// no per-client SSO bundle) — a plain isEnabled is enough. hasDemand("admin") checks the bare
// "admin" string but sfg20 ops users carry "admin-companies" etc. — check that instead. The
// per-client tier-3 gate (feature-identity-provider) and paywall are handled inside IdpPanel.
function idpTabVisible() {
    return hasDemand("admin-companies") && isEnabled(FEATURE_FLAG_SSO)
}

ClientTabs.plug(<Tab label="Identity Provider" value="idp" priority={200} if={idpTabVisible} />)

ClientPanels.plug(
    <TabPanel value="idp" if={idpTabVisible}>
        <IdpPanel $lazy />
    </TabPanel>
)
